// The one way the client talks to the server: same-origin JSON, with a typed error.

import type {
  CreatedDeviceDTO,
  CreatedShareDTO,
  DaysDTO,
  DeviceDTO,
  EntryPageDTO,
  MeDTO,
  OpenedShareDTO,
  RunsDTO,
  SearchDTO,
  SearchKindDTO,
  ShareDTO,
  StatsDTO,
  TrendsDTO,
  WindowDTO,
} from "../shared/dto.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let previewShareId: string | null = null;

/** While set, every request asks to see the app as that share link would. */
export function setPreviewShare(id: string | null) {
  previewShareId = id;
}

async function request<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  // The server refuses non-GET requests that aren't JSON, so every mutation sends a body.
  const body = init.method && init.method !== "GET" ? JSON.stringify(init.body ?? {}) : undefined;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (previewShareId) headers["X-NSJ-Preview"] = previewShareId;
  const res = await fetch(path, { method: init.method ?? "GET", headers, body, credentials: "same-origin" });
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(res.status, "bad_response", `Unexpected response (HTTP ${res.status})`);
  }
  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; message?: string };
    throw new ApiError(res.status, err.error ?? "error", err.message ?? `HTTP ${res.status}`);
  }
  return data as T;
}

export type WindowQuery = { date: string; refresh?: boolean } | { from: number; to: number; refresh?: boolean };

export const api = {
  me: () => request<MeDTO>("/api/me"),
  window(q: WindowQuery) {
    const params = new URLSearchParams("date" in q ? { date: q.date } : { from: String(q.from), to: String(q.to) });
    if (q.refresh) params.set("refresh", "1");
    return request<WindowDTO>(`/api/window?${params}`);
  },
  stats(q: { from: string; to: string; compare: boolean }) {
    const params = new URLSearchParams({ from: q.from, to: q.to, compare: q.compare ? "1" : "0" });
    return request<StatsDTO>(`/api/stats?${params}`);
  },
  days(q: { from: string; to: string }) {
    return request<DaysDTO>(`/api/days?${new URLSearchParams(q)}`);
  },
  trends(q: { by: "week" | "month"; from: string; to: string }) {
    return request<TrendsDTO>(`/api/trends?${new URLSearchParams(q)}`);
  },
  entry(id: string, after: number | null) {
    const params = after ? `?after=${after}` : "";
    return request<EntryPageDTO>(`/api/entry/${encodeURIComponent(id)}${params}`);
  },
  search(q: string, kind: SearchKindDTO) {
    return request<SearchDTO>(`/api/search?${new URLSearchParams({ q, kind })}`);
  },
  runs: () => request<RunsDTO>("/api/runs"),
  shares: {
    list: () => request<ShareDTO[]>("/api/shares"),
    create: (body: { label: string; journal: boolean; notes: boolean; lastDays: number | null; expiresInDays: number | null }) =>
      request<CreatedShareDTO>("/api/shares", { method: "POST", body }),
    revoke: (id: string) => request<{ ok: true }>(`/api/shares/${encodeURIComponent(id)}/revoke`, { method: "POST" }),
    open: (token: string) => request<OpenedShareDTO>("/api/share/open", { method: "POST", body: { token } }),
    leave: () => request<{ ok: true }>("/api/share", { method: "DELETE" }),
  },
  devices: {
    list: () => request<DeviceDTO[]>("/api/devices"),
    create: (label: string) => request<CreatedDeviceDTO>("/api/devices", { method: "POST", body: { label } }),
    revoke: (id: string) => request<{ ok: true }>(`/api/devices/${encodeURIComponent(id)}/revoke`, { method: "POST" }),
  },
  post: <T = { ok: true }>(path: string, body: unknown = {}) => request<T>(path, { method: "POST", body }),
};
